import { NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../../environments/environment';
import { APPOINTMENT_CONSTANT } from '../../../../constants/appointment-constants';
import { TEMPLATE_CONSTANT } from '../../../../constants/template.constant';
import { TRANSLATION_TEMPLATES } from '../../../../constants/translation-templates-constants';
import { MEETING_TYPE_SELECTION_UI_MODE, WIDGET_CONSTANTS } from '../../../../constants/widget-constants';
import { CustomBookingMessageTemplate } from '../../../../db-models/widget-conf-dao';
import { Partner } from '../../../../models/global';
import { MeetingTypeModel } from '../../../../models/meeting-types.model';
import { AppointmentState, UserState } from '../../../../models/state.model';
import { WidgetColorConf } from '../../../../models/widget-color.model';
import { CustomEventService } from '../../../../services/custom-event.service';
import { FormsService } from '../../../../services/forms.service';
import { HelperService } from '../../../../services/helper.service';
import { LoggerService } from '../../../../services/logger.service';
import { PartnerService } from '../../../../services/partner.service';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { CwRadioTickButtonComponent } from '../../../common/theme/cw-radio-tick-button/cw-radio-tick-button.component';
import { AddressFormatPipe } from '../../../../shared/pipes/address-format.pipe';
import { CalioMeetingTemplatePipe } from '../../../../shared/pipes/calio-meeting-template.pipe';
import { TranslationPipe } from '../../../../shared/pipes/translation.pipe';
import { TrustHtmlPipe } from '../../../../shared/pipes/trust-html.pipe';

@Component({
  selector: 'app-appointment-meeting-type',
  imports: [
    TranslationPipe, TrustHtmlPipe, CalioMeetingTemplatePipe, AddressFormatPipe,
    NgSelectModule, FormsModule, TranslateModule, NgTemplateOutlet,
    ButtonComponent, CwRadioTickButtonComponent
  ],
  templateUrl: './appointment-meeting-type.component.html',
  styleUrl: './appointment-meeting-type.component.scss'
})
export class AppointmentMeetingTypeComponent implements OnInit, OnChanges {
  private formService = inject(FormsService);
  private translate = inject(TranslateService);
  private partnerService = inject(PartnerService);
  private helperService = inject(HelperService);
  private customEventService = inject(CustomEventService);

  protected isWhitelabel(): boolean {
    return this.partnerService.hasFeature('is_whitelabel_enabled');
  }

  protected showMeetingTypesAsTiles(): boolean {
    return this.partner?.meeting_type_selection_ui_mode === MEETING_TYPE_SELECTION_UI_MODE.TILES;
  }

  @Input() lang: string;
  @Input() appointmentState: AppointmentState;
  @Input() selectedWorkerIds: number[];
  @Input() userState: UserState;
  @Input() partner: Partner;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() widgetColorConf: WidgetColorConf;
  @Input() nextButtonTemplate: CustomBookingMessageTemplate;
  @Input() previousButtonTemplate: CustomBookingMessageTemplate;
  @Input() subscriptionTypeId: number;

  @Output() navigateToEvent = new EventEmitter<any>();
  @Output() hideMeetingTypesStepValue = new EventEmitter<boolean>();

  protected meetingTypes: MeetingTypeModel[];
  protected isMeetingTypesLoading = false;
  protected readonly ghostTiles = [1, 2, 3, 4];
  protected readonly environment = environment;
  protected readonly templateContent = TRANSLATION_TEMPLATES;
  protected readonly appointmentConstant = APPOINTMENT_CONSTANT;
  protected readonly widgetConstant = WIDGET_CONSTANTS;
  protected readonly templateConstant = TEMPLATE_CONSTANT;
  protected stepErrorTemplate: CustomBookingMessageTemplate;
  protected readonly showStepValidationError = signal(false);

  ngOnInit(): void {
    this.translate.onLangChange.subscribe(lang => this.lang = lang.lang);
    this.appointmentState?.has_meeting_types_restrictions && this.getAppointmentMeetingTypes();
    this.setupStepErrorTemplate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.widgetTemplates?.currentValue) {
      this.widgetTemplates?.length && this.setupStepErrorTemplate();
    }
  }

  private setupStepErrorTemplate(): void {
    this.stepErrorTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_STEP_ERROR_MEETING_TYPE
    );
  }

  onMeetingTypeChange(meetingType: MeetingTypeModel): void {
    this.appointmentState.meeting_type_id = meetingType.id;
    this.userState.meeting_type_id = meetingType.id;
    this.userState.meetingType = meetingType;
  }

  getAppointmentMeetingTypes(): void {
    const workerId = this.appointmentState.worker;
    const serviceIds = this.appointmentState.services;
    this.isMeetingTypesLoading = true;
    // Scope meeting types to the pool only while on "anyone" — a picked worker's own
    // types must win, or the pool intersect hides types only they offer.
    const isAnyoneSelected = !workerId;
    const workerIds = isAnyoneSelected && Array.isArray(this.selectedWorkerIds) && this.selectedWorkerIds.length >= 2
      ? this.selectedWorkerIds
      : [];
    this.formService.getMeetingTypesByAppointmentServiceIds(workerId, serviceIds, undefined, workerIds).subscribe({
      next: meetingTypes => {
        if (meetingTypes?.length) {
          meetingTypes.forEach(meetingType => meetingType.is_multi_language = 1);
        }
        //TODO (Chirag): remove array filter after BE Upgrade
        meetingTypes = meetingTypes.filter(meeting => meeting.id !== 3 && meeting.id !== 14);
        this.meetingTypes = meetingTypes?.length ? meetingTypes : [];
        this.isMeetingTypesLoading = false;
        if (meetingTypes.length === 1) {
          this.hideMeetingTypesStepValue.emit(true);
          this.onMeetingTypeChange(meetingTypes[0]);
          // Coming back from the date step must not auto-advance to it again.
          this.navigateTo(
            this.customEventService.previousPage === this.appointmentConstant.DATE
              ? this.appointmentConstant.APPOINTMENT
              : this.appointmentConstant.DATE
          );
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isMeetingTypesLoading = false;
        LoggerService.error(error);
      }
    });
  }

  navigateTo(page: string): void {
    this.navigateToEvent.next(page);
  }

  protected onNextButtonClick(): void {
    if (
      Number(this.partner?.widget_buttons_always_enabled) === 1 &&
      this.appointmentState?.has_meeting_types_restrictions && !this.appointmentState?.meeting_type_id
    ) {
      this.showStepValidationError.set(true);
      return;
    }
    this.showStepValidationError.set(false);
    this.navigateTo(this.appointmentConstant.DATE);
  }
}
